import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { wa, WA_MESSAGES, scrollToId } from "@/lib/site";

const LINKS = [
  { label: "Why MAHIRA", id: "why-mahira" },
  { label: "A2 Ghee", id: "product" },
  { label: "Reviews", id: "reviews" },
  { label: "Wholesale", id: "wholesale" },
  { label: "FAQ", id: "faq" },
  { label: "Contact", id: "contact" },
];

export default function Navbar({ promoActive = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 z-40 transition-[background-color,box-shadow,border-color,top] duration-500 ${
        promoActive ? "top-9" : "top-0"
      } ${
        scrolled
          ? "border-b border-forest/10 bg-ivory/80 shadow-[0_10px_40px_-20px_rgba(26,54,38,0.25)] backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 md:px-10">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex flex-col items-start leading-none"
        >
          <span className="font-heading text-2xl font-semibold tracking-[0.18em] text-forest">
            MAHIRA
          </span>
          <span className="mt-1 text-[9px] font-semibold uppercase tracking-[0.42em] text-gold">
            Agro Products
          </span>
        </button>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="link-underline text-[13px] font-medium uppercase tracking-[0.18em] text-forest/80 transition-colors duration-300 hover:text-forest"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-whatsapp-cta"
            href={wa(WA_MESSAGES.default)}
            target="_blank"
            rel="noreferrer"
            className="hidden items-center gap-2 rounded-full bg-forest px-5 py-2.5 text-[12px] font-semibold uppercase tracking-[0.16em] text-ivory transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-forest-light sm:inline-flex"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp Us
          </a>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-forest/15 text-forest lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-forest/10 bg-ivory/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="py-3 text-left font-heading text-2xl text-forest transition-colors duration-300 hover:text-gold"
                >
                  {l.label}
                </button>
              ))}
              <a
                data-testid="nav-mobile-whatsapp-cta"
                href={wa(WA_MESSAGES.default)}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-forest px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.16em] text-ivory"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp Us
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
